import { Link } from 'react-router-dom';
import type { InspectionListItem } from '../../lib/api/inspections.gen';
import { formatDistanceToNow } from '../../lib/time';
import { useVocab } from '../../lib/vocab';
import { Cluster, Inline, StatusChip } from '../ui';
import { STATE_LABEL, STATE_TONE, STATE_WEIGHT } from '../inspections/InspectionLifecycleBar';

// Story 2-146b: this used to be a hand-rolled `statusPalette` — raw Tailwind
// bg/text classes on a plain `<span>`, not even a `Badge` — for the SAME
// lifecycle enum `InspectionLifecycleBar` already exports a `StatusChip`
// tone/label/weight map for (`STATE_TONE`/`STATE_LABEL`/`STATE_WEIGHT`).
// Reused rather than re-derived, same discipline `ReviewDetailPage` already
// followed for this exact map (see that file's own comment).

// `propertyId` is the SERVER-resolved `{ _id, name }` ref (story 2-72c's
// `InspectionListItem` DTO, `contracts/inspections.js`) — never a bare
// string any more (that was the pre-2-72c `.populate('propertyId')` shape's
// own ambiguity: a string id, a full Property document, or null when the
// referenced site was deleted). `name` is itself nullable for that same
// dangling-ref case; fall back to the short id the activity feed also uses.
// Exported (story 2-139): the Cards view (`ItemCards.tsx`) and the
// collection's client-side "Name (A–Z)" sort both need the exact same
// null handling this row already carries — one function, not a second copy
// that can drift on the next dangling-ref edge case.
export function propertyLabel(inspection: InspectionListItem): string {
  return inspection.propertyId?.name ?? `#${inspection._id.slice(-4)}`;
}

export function ItemList({ inspections }: { inspections: InspectionListItem[] }): JSX.Element {
  // Vocabulary layer (P03 §5, story 3-2): every inspection is building-domain
  // today, so the unqualified default-domain noun is exact.
  const { vocab } = useVocab();
  const unitsLower = vocab('Units').toLowerCase();
  return (
    <div className="rounded-hairline border border-border bg-card overflow-hidden divide-y divide-white/10">
      {inspections.map((inspection) => (
        // Story 2-147wb: `Cluster` (2-147sa) — was a raw
        // `flex flex-col gap-2 ... sm:flex-row sm:items-center sm:justify-between`
        // div. `Cluster`'s own content-based wrap (stacks the two blocks
        // when they don't fit, sits them side by side when they do) reaches
        // the same "stack narrow, row wide" shape the old breakpoint-based
        // classes hand-derived, without a fixed `sm:` cutoff.
        <Cluster
          key={inspection._id}
          justify="between"
          align="center"
          gap={2}
          className="px-4 py-4"
          // Story 5-1a's browser gate anchors its row locator on this id, never
          // on free text (a text-filtered row locator has matched two rows in
          // this suite before — story 4-20's locator note).
          data-testid={`item-row-${inspection._id}`}
        >
          <div>
            <p className="text-sm font-medium text-foreground">{propertyLabel(inspection)}</p>
            <p className="text-xs text-muted-foreground">
              {`Updated ${formatDistanceToNow(inspection.updatedAt)} • ${inspection.roomsCount} ${unitsLower} captured`}
            </p>
          </div>
          {/* `Inline` (2-147sa), not `Cluster` — this pair must never wrap
              mid-row (the old raw div had no `flex-wrap` either). */}
          <Inline gap={3}>
            <span data-testid={`item-status-${inspection._id}`}>
              <StatusChip
                tone={STATE_TONE[inspection.status]}
                weight={STATE_WEIGHT[inspection.status]}
                label={STATE_LABEL[inspection.status]}
                value={inspection.status}
              />
            </span>
            <Link
              data-testid={`item-open-${inspection._id}`}
              to={`/app/items/${inspection._id}`}
              className="focus-ring inline-flex items-center rounded-full border border-primary/40 px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/10"
            >
              Open detail
            </Link>
          </Inline>
        </Cluster>
      ))}
    </div>
  );
}
